"use client";

import {
  Card,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { useFormikContext } from "formik";
import React from "react";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import INotification from "@/interfaces/notification.interface";
import { NotificationsFiltersMessages } from "@/messages/notifications/notification-filters.messages";
import { FilterType, IFilter } from "@/types/filter.types";

import { NotificationFilterKey } from "../../types";

type Props = {
  messages: NotificationsFiltersMessages;
};

const GRID_SIZES = {
  LARGE: { lg: 4, md: 6, sm: 12, xs: 12 },
  SMALL: { lg: 2, md: 2, sm: 2, xs: 12 },
} as const;

const READ_VALUES = {
  TRUE: "true",
  FALSE: "false",
  EMPTY: "",
} as const;

const READ_BOOLEAN_VALUES = {
  TRUE: true,
  FALSE: false,
} as const;

function FiltersForm({ messages }: Props) {
  const { values, setValues, submitForm, errors } = useFormikContext<IFilter<INotification>[]>();

  const getValueFromProperty = (
    property: string,
    filterValues: IFilter<INotification>[]
  ): IFilter<INotification> | undefined => {
    const filter = filterValues.find((f) => f.property === property);
    return filter;
  };

  const handleFilterChange = (
    property: NotificationFilterKey,
    value: string | number | boolean | null,
    filterType: FilterType
  ) => {
    setValues((prevFilters) => {
      const currentFilters = [...prevFilters];

      const existingFilterIndex = currentFilters.findIndex((f) => f.property === property);
      if (existingFilterIndex !== -1) {
        currentFilters.splice(existingFilterIndex, 1);
      }

      const isEmptyValue =
        value === null || value === "" || (typeof value === "number" && isNaN(value));
      if (isEmptyValue) {
        return currentFilters;
      }

      const newFilter: IFilter<INotification> = {
        property: property as keyof INotification,
        filterType: filterType,
        value: value,
      };

      return [...currentFilters, newFilter];
    });
  };

  const handleReadStatusChange = (event: SelectChangeEvent<string>) => {
    const selectedValue = event.target.value as string;
    const readValue =
      selectedValue === READ_VALUES.TRUE
        ? READ_BOOLEAN_VALUES.TRUE
        : selectedValue === READ_VALUES.FALSE
          ? READ_BOOLEAN_VALUES.FALSE
          : null;
    handleFilterChange("read", readValue, FilterType.Equals);
  };

  const handleDateChange = (value: dayjs.Dayjs | null) => {
    if (!value) {
      setValues((prevFilters) =>
        prevFilters.filter(
          (f) =>
            f.property !== "rowDate" ||
            (f.filterType !== FilterType.GreaterThan && f.filterType !== FilterType.LessThan)
        )
      );
      return;
    }

    const startDate = value.startOf("day").toISOString();
    const endDate = value.endOf("day").toISOString();

    setValues((prevFilters) => {
      const otherFilters = prevFilters.filter(
        (f) =>
          f.property !== "rowDate" ||
          (f.filterType !== FilterType.GreaterThan && f.filterType !== FilterType.LessThan)
      );

      return [
        ...otherFilters,
        {
          property: "rowDate",
          value: startDate,
          filterType: FilterType.GreaterThan,
        },
        {
          property: "rowDate",
          value: endDate,
          filterType: FilterType.LessThan,
        },
      ];
    });
  };

  const handleClearFilters = () => {
    setValues([]);
  };

  const getDateValue = (): dayjs.Dayjs | null => {
    const dateFilter = values.find(
      (f) => f.property === "rowDate" && f.filterType === FilterType.GreaterThan
    );
    return dateFilter ? dayjs(dateFilter.value as string) : null;
  };

  const renderTextField = (
    name: string,
    label: string,
    property: NotificationFilterKey,
    filterType: FilterType = FilterType.Contains
  ) => (
    <Grid size={GRID_SIZES.LARGE}>
      <TextField
        name={name}
        label={label}
        onChange={(e) => handleFilterChange(property, e.target.value, filterType)}
        value={getValueFromProperty(property, values)?.value ?? ""}
        error={!!errors[0] && !!errors[0].value}
        helperText={!!errors[0] && errors[0].value}
        fullWidth
        slotProps={{
          inputLabel: { shrink: true },
        }}
      />
    </Grid>
  );

  return (
    <Card>
      <MDBox p={2}>
        <Grid container spacing={2} alignItems="stretch" wrap="wrap">
          {renderTextField("detail", messages.detailsOption, "detail")}

          <Grid size={GRID_SIZES.LARGE}>
            <FormControl fullWidth error={!!errors[0] && !!errors[0].value}>
              <InputLabel shrink>{messages.readOption}</InputLabel>
              <Select
                value={String(getValueFromProperty("read", values)?.value ?? READ_VALUES.EMPTY)}
                onChange={handleReadStatusChange}
                label={messages.readOption}
                displayEmpty
                inputProps={{ "aria-label": messages.readOption }}
              >
                <MenuItem value={READ_VALUES.EMPTY}>Todos</MenuItem>
                <MenuItem value={READ_VALUES.TRUE}>{messages.yesOption}</MenuItem>
                <MenuItem value={READ_VALUES.FALSE}>{messages.noOption}</MenuItem>
              </Select>
              {errors[0] && errors[0].value && <FormHelperText>{errors[0].value}</FormHelperText>}
            </FormControl>
          </Grid>

          <Grid size={GRID_SIZES.LARGE}>
            <DatePicker
              label={messages.dateTimeOption}
              value={getDateValue()}
              onChange={handleDateChange}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: !!errors[0] && !!errors[0].value,
                  helperText: !!errors[0] && errors[0].value,
                  InputLabelProps: { shrink: true },
                },
              }}
            />
          </Grid>

          <Grid size={12}>
            <Grid container spacing={2} mt={2}>
              <Grid size={GRID_SIZES.SMALL}>
                <MDButton variant="gradient" color="light" onClick={handleClearFilters} fullWidth>
                  Limpar Filtros
                </MDButton>
              </Grid>
              <Grid size={GRID_SIZES.SMALL}>
                <MDButton variant="gradient" color="dark" onClick={submitForm} fullWidth>
                  Filtrar
                </MDButton>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </MDBox>
    </Card>
  );
}

export default FiltersForm;
